"use client";

import { useState } from "react";
import Image from "next/image";
import styles from "./MMVPriceBreakup.module.scss";
import getIndianCurrencyFormat, {
  getIndianCurrencyFormatForMaskedPrice,
} from "@helpers/getIndianCurrencyFormat";
import { captureLead } from "@helpers/leadCapture";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { formatCurrencyINR } from "@utils/numberFormatter";
import Text from "@components/atoms/Text";
import assetLoader from "@utils/internalImageLoder";
import { cn } from "@utils/utils";

interface PriceBreakdownProps {
  exShowroomPrice: string;
  otherCharges: any;
  otherChargesTotal: string;
  onRoadPrice: number;
  ackoDiscount: string;
  ackoDrivePrice: string;
  deliveryDate: string;
  cityName: string;
  isLoggedIn: boolean;
  setIsModalOpen: (val: boolean) => void;
  setIsIntentModalOpen: (val: boolean) => void;
  isInventoryAvailable: boolean;
  deliveryType: string;
  isSellable: boolean;
  pageType?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  carAvailability?: string;
  savingsBreakup?: any;
  selectedColor?: any;
  savingBalance?: any;
  environmentCompensation?: number;
  fuelType?: string;
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears?: string[];
  isUpsellFlow?: boolean;
}

export default function PriceBreakupV2({
  exShowroomPrice,
  otherCharges,
  otherChargesTotal,
  onRoadPrice,
  ackoDiscount,
  ackoDrivePrice,
  deliveryDate,
  cityName,
  isLoggedIn,
  isInventoryAvailable,
  deliveryType,
  setIsModalOpen,
  setIsIntentModalOpen,
  isSellable,
  pageType,
  brandName,
  modelName,
  variantName,
  carAvailability,
  savingsBreakup,
  selectedColor,
  savingBalance,
  environmentCompensation,
  fuelType,
  selectedYear,
  onYearChange,
  availableYears,
  isUpsellFlow = false,
}: PriceBreakdownProps) {
  const [showOtherCharges, setShowOtherCharges] = useState(false);
  const { consumerdiscount: consumerDiscount } = savingsBreakup || {};
  const getRIPPlusConsumer = (savingsBreakup: {
    ripdiscount: any;
    consumerdiscount: any;
  }) => {
    return savingsBreakup?.ripdiscount + savingsBreakup?.consumerdiscount || 0;
  };
  // const [selectedLanguage, setSelectedLanguage] = useState("KA");
  const toggleOtherCharges = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_price_breakup",
        car_availability: carAvailability,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        vehicle_color: selectedColor?.brand_color || "",
        city_name: cityName,
      },
    });
    setShowOtherCharges(!showOtherCharges);
    if (!showOtherCharges && isLoggedIn) {
      await captureLead({
        pageType,
        carAvailability,
        brandName,
        modelName,
        variantName,
        cityName,
        vehicleColor: selectedColor?.brand_color || "",
        loginTypeLabel: "ViewedDetailedprice",
        componentName: "MMVPriceBreakupV2",
      });
    }
  };

  return (
    <div
      className={cn(styles.container, isUpsellFlow && styles.upsellContainer)}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>Price breakup</h2>
        {/* <div className={styles.languageSelector}>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "KA" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("KA")}
          >
            KA
          </button>
          <button
            className={`${styles.languageButton} ${
              selectedLanguage === "BH" ? styles.active : ""
            }`}
            onClick={() => setSelectedLanguage("BH")}
          >
            BH
          </button>
        </div> */}
        {isLoggedIn && availableYears && availableYears.length > 1 && (
          <div className={styles.yearSelector}>
            {availableYears.map((year) => (
              <button
                className={`${styles.yearButton} ${
                  selectedYear === year ? styles.yearButtonActive : ""
                }`}
                onClick={() => onYearChange(year)}
                key={year}
              >
                {year}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.priceCard}>
        <div className={styles.priceComponents}>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>Ex-showroom price</span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(exShowroomPrice)}`}
            </span>
          </div>

          <div className={styles.priceRow} onClick={toggleOtherCharges}>
            <span className={styles.priceLabel}>
              Other charges
              <Image
                src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt="Expand"
                width={18}
                height={18}
                className={`${styles.chevron} ${
                  showOtherCharges ? styles.rotate : ""
                }`}
              />
            </span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(otherChargesTotal)}`}
            </span>
          </div>

          {showOtherCharges && (
            <div className={styles.otherChargesDetails}>
              {otherCharges.map((charge: any, index: number) => (
                <div key={index} className={styles.chargeRow}>
                  <span>{charge.label}</span>
                  <span>{`${formatCurrencyINR(charge.value)}`}</span>
                </div>
              ))}
            </div>
          )}
          {consumerDiscount > 0 && (
            <>
              <div className={styles.divider}></div>
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>Consumer Savings</span>
                <span className={styles.priceValue}>
                  {`₹${getIndianCurrencyFormat(consumerDiscount)}`}
                </span>
              </div>
            </>
          )}

          <div className={styles.divider}></div>
          <div className={styles.priceRow}>
            <span className={styles.priceLabel}>
              {`On-road price in ${cityName}`}
            </span>
            <span className={styles.priceValue}>
              {`₹${getIndianCurrencyFormat(
                isLoggedIn
                  ? Number(onRoadPrice || 0) -
                      getRIPPlusConsumer(savingsBreakup)
                  : onRoadPrice
              )}`}
            </span>
          </div>

          {isSellable && (
            <div className={styles.priceRow}>
              <span className={styles.priceLabel}>ACKO Drive discount</span>
              <span className={styles.discountValue}>
                {` - ₹${
                  isLoggedIn
                    ? getIndianCurrencyFormat(savingsBreakup?.ackodiscount || 0)
                    : getIndianCurrencyFormatForMaskedPrice(
                        savingsBreakup?.ackodiscount || ""
                      )
                }`}
              </span>
            </div>
          )}

          {savingBalance > 0 && (
            <Text className={styles.savingBalanceText}>
              (
              <span className={styles.savingBalanceValue}>
                {formatCurrencyINR(savingBalance)}
              </span>{" "}
              New car savings balance applied)
            </Text>
          )}
        </div>
        {isSellable && (
          <div className={styles.ackoPriceContainer}>
            <div className={styles.ackoPriceRow}>
              <span
                className={styles.ackoPriceLabel}
              >{`ACKO Drive price`}</span>
              <span className={styles.ackoPriceValue}>
                {`₹${
                  isLoggedIn
                    ? getIndianCurrencyFormat(ackoDrivePrice)
                    : getIndianCurrencyFormatForMaskedPrice(ackoDrivePrice)
                }`}
              </span>
            </div>
            {isSellable &&
              (fuelType?.toLowerCase() === "electric" ? (
                <div className={styles.gstSavingsText}>
                  No GST impact on EV cars, prices are the same before and
                  after.
                </div>
              ) : (
                environmentCompensation &&
                environmentCompensation > 1 && (
                  <div className={styles.gstSavingsText}>
                    Good news! This price already includes your GST savings of ₹
                    {getIndianCurrencyFormat(environmentCompensation)}
                  </div>
                )
              ))}

            <div className={styles.infoBox}>
              <p className={styles.infoText}>
                <span className={styles.infoLabel}>{`Did you know? `}</span>
                {`Our prices are lower than dealership price because we don't charge any commissions`}
              </p>
            </div>
          </div>
        )}
      </div>
      {isSellable && (
        <p className={styles.disclaimer}>
          Prices are subject to change based on your delivery date
        </p>
      )}
    </div>
  );
}
